import type { PresetData, CustomSize } from "./transferLabTypes";
import { PRESET_SIZE_CONFIGS } from "./transferLabTypes";
import { scaleToFitRatio } from "../utils/scaleToFitRatio";

// Simple ID generator (can be replaced with nanoid or uuid)
let idCounter = 0;
export function generateId(prefix?: string): string {
  idCounter += 1;
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substr(2, 5);
  const id = `${timestamp}-${random}-${idCounter}`;
  return prefix ? `${prefix}-${id}` : id;
}

// Create preset data with sizes
export function createPresetData(presetId: string, aspectRatio?: number): PresetData {
  const sizeConfigs = PRESET_SIZE_CONFIGS[presetId] || [];

  return {
    id: presetId,
    name: presetId
      .split("-")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" "),
    sizes: sizeConfigs.map((config, index) => {
      // If aspect ratio is provided, scale the dimensions
      let scaledDimensions = { width: config.width, height: config.height };
      if (aspectRatio) {
        scaledDimensions = scaleToFitRatio(config.width, config.height, aspectRatio);
      }

      return {
        id: `${presetId}-size-${index}`,
        size: config.size,
        displayValue: config.displayValue,
        width: scaledDimensions.width,
        height: scaledDimensions.height,
        quantity: 0
      };
    })
  };
}

// Create a new custom size
export function createCustomSize(
  aspectRatio?: number,
  bounds?: {
    minWidthInches: number;
    minHeightInches: number;
    maxWidthInches: number;
    maxHeightInches: number;
  }
): CustomSize {
  const minWidth = bounds?.minWidthInches ?? 1;
  const minHeight = bounds?.minHeightInches ?? 1;
  const maxWidth = bounds?.maxWidthInches ?? 20;
  const maxHeight = bounds?.maxHeightInches ?? 20;

  let width = minWidth;
  let height = minHeight;

  // If we have aspect ratio, calculate dimensions that satisfy both min bounds and aspect ratio
  if (aspectRatio && aspectRatio > 0) {
    // Try width first: use minWidth and calculate height
    const heightFromWidth = minWidth / aspectRatio;

    // Check if this height satisfies minimum height requirement
    if (heightFromWidth >= minHeight && heightFromWidth <= maxHeight) {
      width = minWidth;
      height = Math.round(heightFromWidth * 100) / 100;
    } else {
      // Use minHeight and calculate width
      const widthFromHeight = minHeight * aspectRatio;
      if (widthFromHeight >= minWidth && widthFromHeight <= maxWidth) {
        width = Math.round(widthFromHeight * 100) / 100;
        height = minHeight;
      } else {
        // If neither works, just use the minimum dimensions
        width = minWidth;
        height = minHeight;
      }
    }
  }

  return {
    id: generateId("custom-size"),
    width,
    height,
    quantity: 0
  };
}

// Create default custom preset
export function createCustomPreset(
  transferTypeId: string,
  aspectRatio?: number,
  bounds?: {
    minWidthInches: number;
    minHeightInches: number;
    maxWidthInches: number;
    maxHeightInches: number;
  }
): {
  id: string;
  customSizes: CustomSize[];
} {
  return {
    id: `custom-${transferTypeId}`,
    customSizes: [createCustomSize(aspectRatio, bounds)]
  };
}
